import type { CSSProperties } from 'react'
import { TypographyRole, typographyCssVarName } from '@storefronts/common-types'

export {
    buildTypographyCssVariables,
    getTypographyCssVariableEntries,
    type CssVariableEntry as TypographyCssVariableEntry,
} from '@storefronts/common-types'

export type TypographyRoleStyleFallback = {
    fontFamily?: string
    fontWeight?: string | number
    lineHeight?: string | number
    textTransform?: string
    letterSpacing?: string
}

/**
 * The five CSS properties role-wired by `getTypographyRoleStyle`. Typed
 * via `CSSProperties` so the returned object spreads cleanly into both
 * inline `style={{}}` props and emotion `styled.div({...})` calls.
 */
export type TypographyRoleStyle = Pick<
    CSSProperties,
    | 'fontFamily'
    | 'fontWeight'
    | 'lineHeight'
    | 'textTransform'
    | 'letterSpacing'
>

/**
 * Returns CSS-in-JS style fragments for one of the 16 typography roles.
 *
 * Always emits all five role-aware CSS properties, each wired to its
 * `--sf-typography-<role>-<prop>` custom property and falling back to
 * either the caller's explicit value or `inherit`. Spread into an inline
 * `style={{}}` prop or interpolate into an emotion `css\`\`` template.
 *
 * Behavior:
 * - When the brand has no override for this role: every var falls back,
 *   `inherit` is a no-op (these CSS properties already inherit by
 *   default), and explicit-value fallbacks preserve the component's
 *   current visual.
 * - When the brand has an override for this role: each var resolves to
 *   the configured value and applies uniformly to every role-aware
 *   element, regardless of whether that element previously styled the
 *   property itself.
 *
 * Pass the component's currently explicit values as the fallback so
 * "no override = no visual change" holds. Properties the component
 * doesn't currently style should be omitted from the fallback so they
 * fall back to `inherit`.
 *
 * Call-site conventions (enforced by review, not by types):
 *
 * 1. Spread exactly once per emit. Two spreads of the same role on the
 *    same element re-emit all five properties; the second call's
 *    fallbacks (which usually omit several keys) silently overwrite the
 *    first call's. If the element has both a static base and a
 *    prop-driven override, merge them into a single fallback object and
 *    spread once.
 *
 * 2. Never set a literal `fontFamily`/`fontWeight`/`lineHeight`/
 *    `textTransform`/`letterSpacing` alongside the spread — feed those
 *    values through the `fallback` argument instead. A literal that
 *    appears later in the object will defeat the role entirely, even
 *    when the brand has configured an override.
 *
 * 3. Conventionally place the spread between structural properties
 *    (`display`, `padding`, …) and non-role typography (`fontSize`,
 *    `color`). Cosmetic — those keys don't overlap with the role's five
 *    — but keeps wired call sites scannable.
 */
export function getTypographyRoleStyle(
    role: TypographyRole,
    fallback: TypographyRoleStyleFallback = {},
): TypographyRoleStyle {
    const varName = (prop: string) => typographyCssVarName(role, prop)

    const fallbackOrInherit = (value: string | number | undefined): string =>
        value === undefined ? 'inherit' : String(value)

    // Casts narrow csstype's union types so emotion accepts the var() strings.
    return {
        fontFamily: `var(${varName('font-family')}, ${fallbackOrInherit(fallback.fontFamily)})`,
        fontWeight:
            `var(${varName('font-weight')}, ${fallbackOrInherit(fallback.fontWeight)})` as CSSProperties['fontWeight'],
        lineHeight: `var(${varName('line-height')}, ${fallbackOrInherit(fallback.lineHeight)})`,
        textTransform:
            `var(${varName('text-transform')}, ${fallbackOrInherit(fallback.textTransform)})` as CSSProperties['textTransform'],
        letterSpacing: `var(${varName('letter-spacing')}, ${fallbackOrInherit(fallback.letterSpacing)})`,
    }
}
